import React from 'react';
import PropTypes from 'prop-types';

import { getDateTimeInUserTimeZone, tenantTimeFormat } from 'utils/time';

import momentPropTypes from 'react-moment-proptypes';

import css from './SchedulingConflict.scss';

function DateTimeRange({start, end}) {
    const timeOnly = (time) => time.format(tenantTimeFormat("time"));
    const dateTime = (time) => time.format(tenantTimeFormat("dateTime"));

    const startEndSameDay = start.clone().startOf('day').isSame(end.clone().startOf('day'));
    const endTimeFormat = startEndSameDay ? timeOnly : dateTime;

    return (
        <>
            <time dateTime={start.format()}>
                {dateTime(start)}
            </time>
            {"- "}
            <time dateTime={end.format()}>
                {endTimeFormat(end)}
            </time>
        </>
    );
}
DateTimeRange.propTypes = {
    start: momentPropTypes.momentObj,
    end: momentPropTypes.momentObj,
};

function OccurrenceDescription({startTime, endTime, description, recurring}) {
    return (
        <div className={css.occurrenceDescription}>
            <DateTimeRange
                start={getDateTimeInUserTimeZone(startTime)}
                end={getDateTimeInUserTimeZone(endTime)} />

            {
                recurring &&
                <>
                    <h3 className={css.recurring}>recurring</h3>
                    <span>{description}</span>
                </>
            }
        </div>
    );
}
OccurrenceDescription.propTypes = {
    startTime: PropTypes.string.isRequired,
    endTime: PropTypes.string.isRequired,
    description: PropTypes.string.isRequired,
    recurring: PropTypes.bool.isRequired,
};

function Event({eventTitle, occurrenceDescription}) {
    return (
        <li className={css.event}>
            <h2>{eventTitle}</h2>
            <OccurrenceDescription {...occurrenceDescription} />
        </li>
    );
}
Event.propTypes = {
    eventTitle: PropTypes.string.isRequired,
    occurrenceDescription: PropTypes.shape(OccurrenceDescription.propTypes).isRequired,
};

function TruncatedDisplayWarning({itemCount, totalItemCount, itemNames}) {
    if (itemCount === totalItemCount) return null;

    return (
        <ul className={css.truncatedDisplayWarning}>
            <li>Only displaying a maximum of {itemCount} {itemNames}.</li>
        </ul>
    );
}
TruncatedDisplayWarning.propTypes = {
    itemCount: PropTypes.number.isRequired,
    totalItemCount: PropTypes.number.isRequired,
    itemNames: PropTypes.string.isRequired,
};

function ConflictingEvents({items, totalConflictCount}) {
    return (
        <>
            <ul className={css.conflictingEvents}>
                {items.map((event, index) => <Event key={index} {...event} />)}
            </ul>

            <TruncatedDisplayWarning
                itemCount={items.length}
                totalItemCount={totalConflictCount}
                itemNames="conflicting items" />
        </>
    );
}
ConflictingEvents.propTypes = {
    items: PropTypes.arrayOf(
            PropTypes.shape({
                eventTitle: PropTypes.string.isRequired,
                occurrenceDescription: PropTypes.shape(OccurrenceDescription.propTypes).isRequired,
            })).isRequired,
    totalConflictCount: PropTypes.number.isRequired,
};

// An appointment scheduling conflict error message.
//
// When a staff member tries to edit a scheduling block or an appointment that results
// in a conflict, shows them what time blocks are conflicting, so they have some
// idea of what to do to fix the conflict.
export default function SchedulingConflict({error}) {
    return (
        <section className={css.schedulingConflict}>
            <h1>{error.shortMessage}:</h1>
            <ConflictingEvents items={error.conflictingItems} totalConflictCount={error.totalConflictCount} />
        </section>
    );
}
SchedulingConflict.propTypes = {
    error: PropTypes.shape({
        totalConflictCount: PropTypes.number.isRequired,
        conflictingItems: ConflictingEvents.propTypes.items,
        shortMessage: PropTypes.string.isRequired,
    })
};
